import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ArrowLeft,
  Copy,
  Download,
  FileText,
  Inbox,
  Loader2,
  Paperclip,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { radianErrorMessage, saveBlob } from "@/components/radian/format";
import { useInboxMessage, useInboxMessages } from "@/lib/api/inbox.queries";
import { getInboxMessageRawBlob, type InboxOutcome } from "@/lib/api/inbox";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute("/companies/$companyId/recibidas/buzon")({
  component: InboxPage,
});

const OUTCOME_FILTERS: Array<InboxOutcome | "all"> = [
  "all",
  "fev_imported",
  "duplicate",
  "no_fev_found",
  "parse_failed",
  "unrouted",
  "error",
];

const OUTCOME_BADGE_VARIANT: Record<
  string,
  "success" | "secondary" | "muted" | "warning" | "destructive"
> = {
  fev_imported: "success",
  duplicate: "secondary",
  no_fev_found: "muted",
  parse_failed: "warning",
  unrouted: "warning",
  error: "destructive",
};

function InboxPage() {
  return (
    <RequireAuth>
      <AppShell>
        <InboxContent />
      </AppShell>
    </RequireAuth>
  );
}

function InboxContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [outcome, setOutcome] = useState<InboxOutcome | "all">("all");
  const [openMessageId, setOpenMessageId] = useState<string | null>(null);

  const listQuery = useInboxMessages(
    companyId,
    outcome === "all" ? {} : { outcome },
  );
  const messages = listQuery.data?.messages ?? [];
  const inboxAddress = listQuery.data?.inboxAddress;

  async function onCopyInbox() {
    if (!inboxAddress) return;
    try {
      await navigator.clipboard.writeText(inboxAddress);
      toast.success(t("recibidas.toast.inbox_copied"));
    } catch {
      toast.error(t("recibidas.toast.copy_failed"));
    }
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/recibidas"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("inbox.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("inbox.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("inbox.subtitle")}</p>
          {inboxAddress ? (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="rounded-md bg-muted px-2 py-1 font-mono text-xs">
                {inboxAddress}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2"
                onClick={() => void onCopyInbox()}
              >
                <Copy className="h-3.5 w-3.5" />
                {t("recibidas.list.copy_inbox")}
              </Button>
              <span className="text-xs text-muted-foreground">
                {t("recibidas.list.inbox_hint")}
              </span>
            </div>
          ) : null}
        </div>
        <div className="w-48">
          <Label htmlFor="inbox-outcome">{t("inbox.filter_outcome")}</Label>
          <Select
            value={outcome}
            onValueChange={(v) => setOutcome(v as InboxOutcome | "all")}
          >
            <SelectTrigger id="inbox-outcome">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OUTCOME_FILTERS.map((o) => (
                <SelectItem key={o} value={o}>
                  {o === "all"
                    ? t("inbox.outcomeFilter.all")
                    : t(`inbox.outcome.${o}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {listQuery.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : listQuery.isError ? (
        <ErrorState
          title={t("inbox.error_title")}
          description={t("inbox.error_body")}
          error={listQuery.error}
          onRetry={() => void listQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : messages.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <Inbox className="h-8 w-8 text-primary" />
            </div>
            <div className="max-w-sm space-y-1">
              <h2 className="text-lg font-semibold">{t("inbox.empty_title")}</h2>
              <p className="text-sm text-muted-foreground">{t("inbox.empty_body")}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("inbox.table.received_at")}</TableHead>
                <TableHead>{t("inbox.table.from")}</TableHead>
                <TableHead>{t("inbox.table.subject")}</TableHead>
                <TableHead>{t("inbox.table.outcome")}</TableHead>
                <TableHead>{t("inbox.table.document")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {messages.map((message) => (
                <TableRow
                  key={message.id}
                  className="cursor-pointer"
                  onClick={() => setOpenMessageId(message.id)}
                >
                  <TableCell className="whitespace-nowrap text-sm">
                    {message.receivedAt}
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate text-sm">
                    {message.from ?? "—"}
                  </TableCell>
                  <TableCell className="max-w-[280px]">
                    <p className="truncate text-sm">{message.subject ?? "—"}</p>
                    {message.snippet ? (
                      <p className="truncate text-xs text-muted-foreground">
                        {message.snippet}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <span title={message.outcomeDetail ?? undefined}>
                      <Badge
                        variant={OUTCOME_BADGE_VARIANT[message.outcome] ?? "muted"}
                      >
                        {t(`inbox.outcome.${message.outcome}`, {
                          defaultValue: message.outcome,
                        })}
                      </Badge>
                    </span>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {message.receivedDocumentId ? (
                      <Link
                        to="/companies/$companyId/recibidas/$receivedId"
                        params={{
                          companyId,
                          receivedId: message.receivedDocumentId,
                        }}
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                      >
                        <FileText className="h-3.5 w-3.5" />
                        {t("inbox.table.open_document")}
                      </Link>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <InboxMessageDialog
        companyId={companyId}
        messageId={openMessageId}
        onClose={() => setOpenMessageId(null)}
      />
    </div>
  );
}

function InboxMessageDialog({
  companyId,
  messageId,
  onClose,
}: {
  companyId: string;
  messageId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const detailQuery = useInboxMessage(companyId, messageId ?? undefined);
  const [downloading, setDownloading] = useState(false);

  const message = detailQuery.data;

  async function onDownloadRaw() {
    if (!messageId) return;
    setDownloading(true);
    try {
      const blob = await getInboxMessageRawBlob(companyId, messageId);
      saveBlob(blob, `${messageId}.eml`);
    } catch (err) {
      toast.error(radianErrorMessage(err, t));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <Dialog open={!!messageId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("inbox.detail.title")}</DialogTitle>
          <DialogDescription>
            {message?.subject ?? t("inbox.detail.no_subject")}
          </DialogDescription>
        </DialogHeader>
        {detailQuery.isPending ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : detailQuery.isError || !message ? (
          <p className="text-sm text-destructive">
            {radianErrorMessage(detailQuery.error, t)}
          </p>
        ) : (
          <div className="flex flex-col gap-3 text-sm">
            <div className="space-y-1 text-xs text-muted-foreground">
              <p>
                {t("inbox.detail.from")}: {message.from ?? "—"}
              </p>
              <p>
                {t("inbox.detail.to")}: {message.to}
              </p>
              <p>
                {t("inbox.detail.received_at")}: {message.receivedAt}
              </p>
              {message.outcomeDetail ? (
                <p>
                  {t("inbox.detail.outcome_detail")}: {message.outcomeDetail}
                </p>
              ) : null}
            </div>
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t("inbox.detail.attachments")}
              </p>
              {message.attachments.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  {t("inbox.detail.no_attachments")}
                </p>
              ) : (
                <ul className="divide-y rounded-md border">
                  {message.attachments.map((attachment, idx) => (
                    <li
                      key={`${attachment.filename}-${idx}`}
                      className="flex items-center gap-2 px-3 py-2 text-xs"
                    >
                      <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="truncate">{attachment.filename}</span>
                      <span className="ml-auto shrink-0 text-muted-foreground">
                        {Math.max(1, Math.round(attachment.size / 1024))} KB
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void onDownloadRaw()}
                disabled={downloading}
              >
                {downloading ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                {t("inbox.detail.download_eml")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
